import { environmentApiRef } from '@agilelab/plugin-wb-environment';
import {
  customAlertApiRef,
  useEnvironmentsContext,
  WbButton,
  WbLink,
  WbSelect,
  WbTooltip,
  WbAlert,
} from '@agilelab/plugin-wb-platform';
import {
  getHttpResponseErrorMetadata,
  snakeCaseToTitleCase,
} from '@agilelab/plugin-wb-platform-common';
import {
  identityApiRef,
  useApi,
  useRouteRef,
} from '@mainstage/core-plugin-api';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import useAsync from 'react-use/lib/useAsync';
import { panelCatalogApiRef } from '../../api';
import { QuickDeployProgress } from '../../apis/models/quickDeploy';
import { releaseDetailRouteRef } from '../../routes';
import { QuickDeployStepper } from './QuickDeployStepper';

export function QuickDeployButton({
  projectUrn,
  projectName,
  projectEntityName,
}: {
  projectUrn: string;
  projectName: string;
  projectEntityName: string;
}): JSX.Element | null {
  const api = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);
  const environmentApi = useApi(environmentApiRef);
  const identityApi = useApi(identityApiRef);
  const navigate = useNavigate();
  const releaseDetailRoute = useRouteRef(releaseDetailRouteRef);
  const { environment } = useEnvironmentsContext();
  const [open, setOpen] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>();
  const accessibleEnvironments = useAsync(async () => {
    const credentials = await identityApi.getCredentials();
    return environmentApi.getAccessibleEnvironments(credentials);
  }, [environmentApi, identityApi]);
  const permissionKey = JSON.stringify([
    projectUrn,
    accessibleEnvironments.value?.map(accessible => accessible.name),
  ]);
  const [permittedEnvironments, setPermittedEnvironments] = useState<{
    key: string;
    names: string[];
  }>();
  const environments = (accessibleEnvironments.value ?? []).filter(
    accessible =>
      permittedEnvironments?.key === permissionKey &&
      permittedEnvironments.names.includes(accessible.name),
  );
  const environmentName =
    environments.find(
      accessible =>
        accessible.name === (selectedEnvironment ?? environment?.name),
    )?.name ?? environments[0]?.name;
  const environmentLabel = snakeCaseToTitleCase(environmentName ?? '');
  const requestKey = JSON.stringify([projectUrn, environmentName]);
  const [eligibleKey, setEligibleKey] = useState<string>();
  const [failedKey, setFailedKey] = useState<string>();
  const [approvalBlockedKey, setApprovalBlockedKey] = useState<string>();
  const [operation, setOperation] = useState<{
    operationId: string;
    environment: string;
  }>();
  const [operationFinished, setOperationFinished] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const activityOpenRef = useRef(false);
  const currentOperationId = useRef<string>();
  const [checkVersion, setCheckVersion] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const submissionInFlight = useRef(false);
  const mounted = useRef(true);
  const operationPending = !!operation && !operationFinished;

  const setActivityVisibility = (visible: boolean) => {
    activityOpenRef.current = visible;
    setActivityOpen(visible);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const navigateToDeploymentPage = useCallback(
    (progress: QuickDeployProgress) => {
      if (!progress.resultingVersion || !operation) {
        alertApi.post({
          message: 'Quick deploy successfully submitted',
          severity: 'success',
        });
        return;
      }
      const redirectOnCompletion = activityOpenRef.current;
      const isCurrentOperation = () =>
        mounted.current && currentOperationId.current === progress.operationId;
      const findRelease = async () => {
        // The release list may lag behind deployment submission.
        for (let attempt = 0; attempt < 10; attempt++) {
          if (!isCurrentOperation()) return undefined;
          const releases = await api.fetchReleases(projectEntityName);
          const release = releases.find(
            candidate =>
              candidate.metadata.version === progress.resultingVersion,
          );
          if (release || !isCurrentOperation()) return release;
          if (attempt < 9)
            await new Promise(resolve => setTimeout(resolve, 1000));
        }
        return undefined;
      };
      findRelease().then(
        release => {
          if (!isCurrentOperation()) return;
          if (!release) {
            alertApi.post({
              severity: 'warning',
              message:
                'Deployment submitted, but the release page is not available yet. Open it from the release list.',
            });
            return;
          }
          const pathname = releaseDetailRoute({
            kind: 'Release',
            namespace: release.metadata.namespace ?? 'default',
            name: release.metadata.name,
          });
          const search = new URLSearchParams({
            env: operation.environment,
          }).toString();
          const deploymentPage = `${pathname}?${search}`;
          if (redirectOnCompletion && activityOpenRef.current) {
            alertApi.post({
              message: 'Quick deploy successfully submitted',
              severity: 'success',
            });
            navigate(deploymentPage);
          } else {
            alertApi.post({
              message: 'A quick deploy successfully completed:',
              actions: (
                <WbLink to={deploymentPage}>
                  {projectName} {progress.resultingVersion}
                </WbLink>
              ),
              severity: 'success',
            });
          }
        },
        error => {
          if (isCurrentOperation()) alertApi.post({ error, severity: 'error' });
        },
      );
    },
    [
      alertApi,
      api,
      navigate,
      operation,
      projectEntityName,
      projectName,
      releaseDetailRoute,
    ],
  );

  const finishOperation = useCallback(
    (progress: QuickDeployProgress) => {
      if (!mounted.current) return;
      setOperationFinished(true);
      setEligibleKey(undefined);
      setCheckVersion(current => current + 1);
      if (progress.currentStep === 'failed') {
        alertApi.post({
          error: new Error(progress.error || progress.message),
          severity: 'error',
        });
      } else {
        navigateToDeploymentPage(progress);
      }
    },
    [alertApi, navigateToDeploymentPage],
  );

  useEffect(() => {
    const controller = new AbortController();
    setPermittedEnvironments(undefined);
    if (accessibleEnvironments.value) {
      Promise.all(
        accessibleEnvironments.value.map(async accessible => {
          try {
            await api.precheckQuickDeployPermissions(
              projectUrn,
              accessible.name,
              {
                signal: controller.signal,
              },
            );
            return accessible.name;
          } catch (error) {
            if (controller.signal.aborted) return undefined;
            if (getHttpResponseErrorMetadata(error)?.httpStatus !== 403) {
              const details =
                error instanceof Error
                  ? error.message || 'Unknown error'
                  : String(error);
              alertApi.post({
                error: new Error(
                  `Unable to verify Quick Deploy prechecks. Details: ${details}`,
                  { cause: error },
                ),
                severity: 'error',
              });
            }
            return undefined;
          }
        }),
      ).then(names => {
        if (!controller.signal.aborted)
          setPermittedEnvironments({
            key: permissionKey,
            names: names.filter((name): name is string => name !== undefined),
          });
      });
    }
    return () => controller.abort();
  }, [
    api,
    alertApi,
    projectUrn,
    accessibleEnvironments.value,
    permissionKey,
    checkVersion,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    setEligibleKey(undefined);
    setFailedKey(undefined);
    setApprovalBlockedKey(undefined);
    if (open && environmentName) {
      api
        .precheckQuickDeployApproval(projectUrn, environmentName, {
          signal: controller.signal,
        })
        .then(
          () => {
            if (!controller.signal.aborted) setEligibleKey(requestKey);
          },
          error => {
            if (controller.signal.aborted) return;
            setFailedKey(requestKey);
            const body = getHttpResponseErrorMetadata(error)?.responseBody;
            if (
              body &&
              typeof body === 'object' &&
              'code' in body &&
              body.code === 'DEPLOY_APPROVAL_REQUIRED'
            )
              setApprovalBlockedKey(requestKey);
          },
        );
    }
    return () => controller.abort();
  }, [api, projectUrn, environmentName, requestKey, open, checkVersion]);

  const disabled =
    !environmentName ||
    accessibleEnvironments.loading ||
    !!accessibleEnvironments.error ||
    eligibleKey !== requestKey ||
    operationPending ||
    submitting;
  const headerDisabled = submitting;
  let tooltip =
    'Create or update a new snapshot and start a deployment operation';
  if (operationPending)
    tooltip =
      'A quick deploy is currently running. Click to check the progress';
  else if (headerDisabled)
    tooltip =
      'Quick deploy unavailable. Contact the platform team for assistance';

  const deploy = async () => {
    if (
      !open ||
      disabled ||
      submissionInFlight.current ||
      !environmentName ||
      !environments.some(accessible => accessible.name === environmentName)
    )
      return;
    submissionInFlight.current = true;
    setSubmitting(true);
    try {
      const accepted = await api.submitQuickDeploy(projectUrn, environmentName);
      if (!mounted.current) return;
      currentOperationId.current = accepted.operationId;
      setOperation({
        operationId: accepted.operationId,
        environment: environmentName,
      });
      setOperationFinished(false);
      setOpen(false);
      setActivityVisibility(true);
    } catch (error) {
      if (mounted.current) {
        const body = getHttpResponseErrorMetadata(error)?.responseBody;
        const message =
          body && typeof body === 'object' && 'userMessage' in body
            ? body.userMessage
            : undefined;
        alertApi.post({
          error:
            typeof message === 'string' && message.trim()
              ? new Error(message, { cause: error })
              : error,
          severity: 'error',
        });
      }
    } finally {
      submissionInFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  const closeDialog = () => {
    if (submissionInFlight.current) return;
    setOpen(false);
    setSelectedEnvironment(undefined);
  };

  if (!operation && environments.length === 0) return null;

  return (
    <>
      {(operationPending || environments.length > 0) && (
        <WbTooltip title={tooltip}>
          <span style={{ display: 'inline-flex' }}>
            <WbButton
              label={operationPending ? 'View Quick Deploy' : 'Quick Deploy'}
              icon={<FlashOnIcon />}
              disabled={headerDisabled}
              loading={submitting}
              onClick={() =>
                operationPending ? setActivityVisibility(true) : setOpen(true)
              }
            />
          </span>
        </WbTooltip>
      )}
      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        aria-labelledby="quick-deploy-dialog-title"
      >
        <DialogTitle id="quick-deploy-dialog-title" disableTypography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Quick Deploy</Typography>
            <WbButton
              variant="text"
              icon={<CloseIcon />}
              aria-label="Close Quick Deploy"
              onClick={closeDialog}
              disabled={submitting}
            />
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box py={2}>
            <Typography variant="h6">You are about to deploy:</Typography>
            <Typography variant="h6">
              <Box component="span" fontWeight={600}>
                {projectName}
              </Box>
            </Typography>
            <Box my={3}>
              <WbSelect
                label="Environment"
                fullWidth
                value={environmentName ?? ''}
                options={environments.map(accessible => accessible.name)}
                getOptionLabel={snakeCaseToTitleCase}
                disabled={submitting || accessibleEnvironments.loading}
                onChange={event => setSelectedEnvironment(event.target.value)}
              />
            </Box>
            <Divider />
            <Box mt={2}>
              <Typography>
                <Box component="span" fontWeight={600}>
                  Attention: the environment is set to {environmentLabel}.
                </Box>
              </Typography>
              <Typography>
                A draft will be created or updated and deployed. It will not be
                promoted.
              </Typography>
            </Box>
            {failedKey === requestKey && (
              <Box mt={2}>
                <WbAlert
                  message={{
                    type: 'warning',
                    title: 'Warning',
                    content:
                      'Quick Deploy is not available for this environment.',
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <WbButton
            label="Cancel"
            variant="outlined"
            icon={<CloseIcon />}
            onClick={closeDialog}
            disabled={submitting}
          />
          <WbTooltip
            title={
              approvalBlockedKey === requestKey
                ? 'An approval workflow is blocking deployment in this environment. Use the standard release flow to request approval.'
                : ''
            }
          >
            <span style={{ display: 'inline-flex' }}>
              <WbButton
                label="Deploy"
                icon={<FlashOnIcon />}
                disabled={disabled}
                loading={submitting}
                onClick={deploy}
              />
            </span>
          </WbTooltip>
        </DialogActions>
      </Dialog>
      <Dialog
        open={activityOpen}
        keepMounted
        onClose={() => setActivityVisibility(false)}
        fullWidth
        maxWidth="lg"
        aria-labelledby="quick-deploy-activity-title"
      >
        <DialogTitle id="quick-deploy-activity-title" disableTypography>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h6">Quick Deploy Activity</Typography>
            <WbButton
              variant="text"
              icon={<CloseIcon />}
              aria-label="Close Quick Deploy activity"
              onClick={() => setActivityVisibility(false)}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {operation && (
            <>
              <Typography variant="subtitle1">
                {projectName} / {snakeCaseToTitleCase(operation.environment)}
              </Typography>
              <QuickDeployStepper
                key={operation.operationId}
                projectUrn={projectUrn}
                operationId={operation.operationId}
                onFinished={finishOperation}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <WbButton
            label="Close"
            variant="outlined"
            onClick={() => setActivityVisibility(false)}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
